import { Component, Input, Output, EventEmitter } from "@angular/core";
import { FormGroup, FormBuilder } from "@angular/forms";
import { EdpTable } from "./edp-table";
import { ApiService } from "@edp-services/api.service";
import { Observable } from "rxjs/Observable";
import { EdpResponseBody } from "@edp-models/response.model";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "@edp-environments/environment";
import { EdpTableOrder, EdpTablePaginator, EdpTableContext } from "./edp-table.model";
import { EdpTableService } from "./edp-table.service";

@Component({
    selector: "app-edp-table",
    templateUrl: "./edp-table.component.html",
    styleUrls: ["./edp-table.component.scss"],
})
export class EdpTableComponent {
    _source: EdpTable;

    @Input() set source(source: EdpTable) {
        if (source) {
            this._source = source;

            this.getColumns();
            this.getOrder();
            this.getData();
            this.noResultMessage = this._source.getNoResultsMessage();
            this.actions = this._source.getActions();
        }
    }
    @Output() loading: EventEmitter<boolean> = new EventEmitter<boolean>();

    public pageSize: number;
    public initialDataSource: Array<any> = [];
    public currentDataSource: Array<any> = [];
    public dataSource: Array<any> = [];
    public actions: Array<any> = [];
    public columnsMetaData: Array<any>;
    public orderList: Array<EdpTableOrder>;
    public noResultMessage: object;
    public nextData = 20;
    public screenWidth: number;
    public isApp: boolean;

    dropdownFormControl: FormGroup = this.formBuilder.group({
        dropdown: null,
    });
    inputSearch: FormGroup = this.formBuilder.group({
        input: null,
    });

    public paginator: EdpTablePaginator = new EdpTablePaginator();

    constructor(
        private formBuilder: FormBuilder,
        private apiService: ApiService,
        private edpTableService: EdpTableService,
        private translate: TranslateService
    ) {
        this.isApp = environment.isApp;
    }

    public getData() {
        this.loading.emit(true);
        this.getDataSource().subscribe(
            (data: EdpResponseBody) => {
                if (data.Success && data.Result.Collection.length) {
                    const dataBeforeRender: any = data.Result.Collection
                        ? this._source.executeBeforeRender(data.Result.Collection)
                        : null;
                    this.initialDataSource = [...dataBeforeRender];
                    this.currentDataSource = dataBeforeRender;

                    this.applyOrder(this.orderList[0]);

                    this.paginator = this.getPaginator();

                    this.setData();
                }
                this.loading.emit(false);
            },
            error =>  {
                console.log(error);
            },
        );
    }

    private getDataSource(): Observable<Error | EdpResponseBody> {
        const args = this.getArgs();

        if (this._source.getEndpoint()) {
            return this.apiService.callServicePost(this._source.getEndpoint(), args, null);
        } else {
            return this._source.getDataSource(args);
        }
    }

    private getColumns(): void {
        this.columnsMetaData = this._source.getColumns();
    }

    private getOrder(): void {
        this.orderList = this._source.getOrder();
    }

    private getArgs(): object {
        const args = {};
        Object.assign(args, this._source.getArgs());

        return args;
    }

    public applyOrder(order: EdpTableOrder): void {
        this.currentDataSource.sort((a, b) => {
            if (a[order.field] > b[order.field]) {
                return order.direction === "desc" ? -1 : 1;
            }
            if (a[order.field] < b[order.field]) {
                return order.direction === "desc" ? 1 : -1;
            }
            return 0;
        });

        this.onPageChange(1);
    }

    public onKeyPress(event: KeyboardEvent, searchText) {
        if (event.key === "Enter" || searchText.length >= 3 || !searchText.length) {
            this.search(searchText);
        }
    }

    public search(searchText: string): void {
        const searchableColumns = this._source.getSearchableColumns();

        let filteredData = [...this.initialDataSource];

        if (searchText) {
            filteredData = filteredData.filter(resultData => {
                let resultFound = false;

                searchableColumns.forEach(column => {
                    let term: string = resultData[column.name] ? resultData[column.name].toLowerCase() : null;
                    if (term && column.searchable) {
                        term = this.translate.instant(resultData[column.name]).toLowerCase();
                    }

                    if (!resultFound && term && term.indexOf(searchText.toLowerCase()) !== -1) {
                        resultFound = true;
                    }
                });

                return resultFound;
            });
        }

        this.currentDataSource = filteredData;
        const currentOrder = this.orderList.filter(item => item.label === this.dropdownFormControl.controls.dropdown.value);
        const orderBy: EdpTableOrder = currentOrder.length ? currentOrder[0] : this.orderList[0];

        this.applyOrder(orderBy);
    }

    public evalColumnClass(column: any, item: any): string {
        let classNames: string;
        if (column.columnClass) {
            classNames = column.columnClass(item);
        }

        return classNames;
    }

    public onPageChange(event: number) {
        this.paginator.currentPage = event;
        this.setData();
    }

    public setData(): void {
        const start: number = (this.paginator.currentPage - 1) * this.paginator.itemsPerPage;
        const end: number = start + this.paginator.itemsPerPage;

        this.dataSource = this.currentDataSource
          ? this.currentDataSource.slice(start, end)
          : [];

        this._source.executeAfterListUpdateFunction(this.dataSource);

        this.paginator.totalItems = this.currentDataSource.length;
        this.paginator.countItems = (this.paginator.currentPage - 1) * this.paginator.itemsPerPage + 1;
        this.paginator.countTotalItems =
            (this.paginator.currentPage - 1) ===  Math.floor(this.paginator.totalItems / this.paginator.itemsPerPage)
                ? this.paginator.totalItems
                : this.paginator.itemsPerPage * this.paginator.currentPage;
    }
    private setDataMobile(start: number, next: number): void {
        this.dataSource = this.currentDataSource
          ? this.currentDataSource.slice(start, next)
          : [];

        this._source.executeAfterListUpdateFunction(this.dataSource);
    }

    public onScroll(): void {
        this.nextData += 20;
        this.setDataMobile(0, this.nextData);
    }

    public runAction(action: any, item: any, rowIndex: number, $event) {
        const context: string = this._source.getContext();

        if (context) {
            const tableContext = new EdpTableContext(
                this.paginator.currentPage,
                window.scrollY
            );

            this.edpTableService.setContext(context, tableContext);
        }

        action.callback(item, rowIndex, $event);
    }

    public runActionCondition(action: any, item: any, rowIndex: number) {
        return action.condition(item, rowIndex);
    }

    private getPaginator(): EdpTablePaginator {
        const context: string = this._source.getContext();
        let currentPage = 1;
        if (context) {
            const contextData = this.edpTableService.getContext(context);

            if (contextData && contextData.page) {
                currentPage = contextData.page;
                setTimeout(() => {
                    window.scrollTo(0, contextData.positionY);
                }, 100);
            }
        }

        return new EdpTablePaginator(
            this._source.getPageSize(),
            currentPage,
            this.currentDataSource.length
        );
    }
}
